// Emitting analytics events, for both the Inertia and the Unpoly frontends. What gets emitted where is
// defined in doc/tracking/; every call site names its catalogue key in a comment (`tracking: <key>`).

import type { Attachment } from 'svelte/attachments'
import { inOverlay } from 'shared/javascripts/util/unpoly_compat'
import { on } from 'shared/javascripts/util/events'
import { whenInView } from 'shared/javascripts/util/in_view'
import { trackEvent } from '../util/analytics'
import type { TrackEventOptions } from '../util/analytics'
export type { TrackEventOptions, TrackEventOverrides } from '../util/analytics'
export { trackingContextAttribute, type TrackingContext } from './dom'
export { trackOnNextPage, flushNextPageEvents } from './next_page'
export type EventData = Record<string, unknown>

/** An element-bound entry of the catalogue: its fixed element.id, plus the variable params it lists. */
export interface Interaction {
  id: string
  params?: EventData
  /** What to send as element.text instead of the element's own text. */
  text?: string
}

/** For the entries whose event is not the attachment's default, e.g. `click.link` instead of `click.element`. */
export interface InteractionWithEvent extends Interaction {
  event?: string
}

export function track(event: string, data: EventData = {}, options: TrackEventOptions = {}): void {
  trackEvent(event, data, options)
}

export function trackInteraction(
  event: string,
  interaction: Interaction,
  element: HTMLElement,
): void {
  track(
    event,
    {
      element: {
        id: interaction.id,
        text: interaction.text ?? elementText(element),
        modal: inOverlay() || element.closest('dialog') !== null,
      },
      ...interaction.params,
    },
    { from: element },
  )
}

/**
 * `{@attach trackClick({ id: '…' })}` — a `click.element` for every click on the element.
 *
 * `event: 'click.link'` for the entries the catalogue puts under that event; the link's href is then sent
 * as `link.href`, so no call site has to repeat it.
 */
export function trackClick(interaction: InteractionWithEvent): Attachment<HTMLElement> {
  return (element) => {
    const handler = () => trackLinkAwareInteraction(interaction, element)
    element.addEventListener('click', handler)
    return () => element.removeEventListener('click', handler)
  }
}

/**
 * `{@attach trackInput({ id: '…' })}` — one `change.input` when the element is first typed into.
 *
 * The event says that a field was used, never what was entered; `element.text` is the field's label, not
 * its value.
 */
export function trackInput(interaction: Interaction): Attachment<HTMLElement> {
  return (element) => {
    const handler = () => {
      element.removeEventListener('input', handler)
      trackInteraction('change.input', { text: '', ...interaction }, element)
    }
    element.addEventListener('input', handler)
    return () => element.removeEventListener('input', handler)
  }
}

/**
 * `{@attach delegateClick(handler)}` — a click listener on a container, for controls the component does not
 * render itself: links a child component owns, the rows of a list. An `onclick` on the container would do the
 * same, but makes a `<nav>` or `<ul>` look interactive to the a11y rules, which it is not.
 */
export function delegateClick(handler: (event: MouseEvent) => void): Attachment<HTMLElement> {
  return (element) => {
    element.addEventListener('click', handler)
    return () => element.removeEventListener('click', handler)
  }
}

/** For components with an optional `tracking` prop: an attachment is a function, so absence needs one too. */
export const noTracking: Attachment<HTMLElement> = () => {}

/** A click-ish push: `click.element`, or `click.link` with the link's href alongside. */
export function trackLinkAwareInteraction(
  interaction: InteractionWithEvent,
  element: HTMLElement,
): void {
  const { event = 'click.element', ...rest } = interaction
  const href = element instanceof HTMLAnchorElement ? linkHref(element.href) : undefined
  const params = event === 'click.link' && href ? { link: { href }, ...rest.params } : rest.params

  trackInteraction(event, { ...rest, params }, element)
}

/**
 * A link target as `link.href` carries it: the path for this site, as Unpoly's links report it
 * (boot/track_links), the full URL for another one.
 */
export function linkHref(href: string): string {
  const url = new URL(href, location.href)
  return url.origin === location.origin ? url.pathname + url.search + url.hash : url.href
}

type VisibleInteraction = Interaction & {
  key?: unknown
  /** For the entries the catalogue gives a visibility event of their own, e.g. `nativeBanner.visible`. */
  event?: string
}

/**
 * `{@attach trackVisible({ id: '…', key })}` — one `element.visible` when the element first comes into view.
 *
 * Fires once per attachment. Svelte re-creates an inline attachment whenever its argument's dependencies
 * change, so anything that identifies *this* showing of the element — the actions a prompt offers, the block
 * it sits in — goes into `key` (or the params), and a new showing is tracked again.
 */
export function trackVisible(interaction: VisibleInteraction): Attachment<HTMLElement> {
  return (element) =>
    whenInView(element, () =>
      trackInteraction(interaction.event ?? 'element.visible', interaction, element),
    )
}

/**
 * `{@attach trackAdVisible({ … })}` — like `trackVisible`, but only once an ad started playing in a player
 * inside the element: a slot whose ad is still loading or never came is not a sighting of an ad.
 */
export function trackAdVisible(interaction: VisibleInteraction): Attachment<HTMLElement> {
  return (element) => {
    let stopTracking: ReturnType<Attachment<HTMLElement>>
    const stopListening = on(
      element,
      'app:player:adStarted',
      () => {
        stopTracking = trackVisible(interaction)(element)
      },
      { once: true },
    )
    return () => {
      stopListening()
      stopTracking?.()
    }
  }
}

// Icon-only controls have no text of their own, but their accessible name says what they do.
function elementText(element: HTMLElement): string {
  const text = element.textContent?.trim().replace(/\s+/g, ' ') ?? ''
  return text || (element.getAttribute('aria-label') ?? '')
}
